import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAppContext } from './store';
import { supabase, testSupabaseConnection } from './lib/supabaseClient';
import { useToast } from './hooks/useToast';
import { useSearch } from './hooks/useSearch';
import { useCart } from './hooks/useCart';
import { useFilters } from './hooks/useFilters';
import { MESSAGES, APP_CONFIG } from './constants';
import { logger } from './utils/logger';
import { trackEvent, trackPageView, initializeGoogleAnalytics, disableGoogleAnalytics } from './utils/googleAnalytics';
import { initializeGoogleAds } from './utils/googleAds';
import { trackHashtagClicked, trackActViewed, trackThemePageViewed } from './utils/analyticsEvents';
import { updatePageMeta, updateJsonLd, generateActSchema, generateThemeSchema } from './utils/seoUtils';
import { initPerformanceMonitoring } from './utils/performanceMonitoring';
import { preloadImages, CRITICAL_IMAGES } from './utils/imageOptimization';
import {
  generateBreadcrumb,
  findActBySlug,
  generateActPageMeta,
  generateThemePageMeta,
  deslugifyTheme,
  generateSlug
} from './utils/urlUtils';
import { detectObsoleteUrlPattern } from './utils/urlRedirects';

// Components
import ErrorBoundary from './components/ErrorBoundary';
import Toast from './components/Toast';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import MobileFilterDrawer from './components/MobileFilterDrawer';
import ResultsGrid from './components/ResultsGrid';
import Pagination from './components/Pagination';
import VisualFilterVignettes from './components/VisualFilterVignettes';
import AppinesBanner from './components/AppinesBanner';
import CookieConsentBanner from './components/CookieConsentBanner';
import StickySimulatorCTA from './components/StickySimulatorCTA';
import CotationPage from './components/CotationPage';
import CotationValuePage from './components/CotationValuePage';
import HashtagPage from './components/HashtagPage';
import Breadcrumb from './components/Breadcrumb';
import Footer from './components/Footer';
import NotFoundPage from './components/NotFoundPage';
import RevalorisationsPage from './components/RevalorisationsPage';
import SeoContentBlock from './components/SeoContentBlock';
import EmbeddedRevenueSimulator from './features/simulator/components/EmbeddedRevenueSimulator';

// Pages
import SimulatorPage from './features/simulator/SimulatorPage';

// Icons
import { Filter, Moon, Sun, Calculator, LayoutGrid } from 'lucide-react';

// ActPage component (previously in AppRouter)
const ActPage = () => {
  const { letter_key, act_slug } = useParams<{ letter_key: string; act_slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const { acts } = state.data;
  const { cartItems } = state.searchAndFilter;
  const { darkMode } = state.ui;

  const [act, setAct] = useState<any>(null);
  const [hasTrackedPageView, setHasTrackedPageView] = useState(false);

  useEffect(() => {
    if (letter_key && act_slug) {
      const foundAct = findActBySlug(acts, letter_key, act_slug);
      if (foundAct) {
        setAct(foundAct);
        const meta = generateActPageMeta(foundAct);
        updatePageMeta(undefined, meta);

        // Update JSON-LD structured data
        const jsonLdSchema = generateActSchema(foundAct);
        updateJsonLd(jsonLdSchema, 'act-page-jsonld');

        // Track page view only once
        if (!hasTrackedPageView) {
          trackPageView(`${foundAct.lettre_cle} ${foundAct.cotation_valeur}`, location.pathname);
          trackActViewed(foundAct.libelle || foundAct.lettre_cle || 'unknown');
          setHasTrackedPageView(true);
        }
      } else {
        // Act not found, redirect to letter key page
        navigate(`/cotations/actes/${letter_key.toLowerCase()}`, { replace: true });
      }
    }
  }, [letter_key, act_slug, acts, navigate, location.pathname, hasTrackedPageView]);

  if (!act) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
            Chargement de l'acte...
          </p>
        </div>
      </div>
    );
  }

  const breadcrumbItems = generateBreadcrumb(letter_key, act);

  return (
    <div className="min-h-screen transition-colors duration-300" style={{
      background: darkMode
        ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <AppinesBanner className="md:pt-6" />

      <div className="pt-20 md:pt-0 px-4 pb-8">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb items={breadcrumbItems} darkMode={darkMode} />
          
          <div className="mb-8">
            <ResultsGrid
              results={[act]}
              cartItems={cartItems}
              onToggleCartItem={(act) => dispatch({ type: 'TOGGLE_CART_ITEM', payload: act })}
              darkMode={darkMode}
              loading={false}
              displayCotationType={state.searchAndFilter.displayCotationType}
              onHashtagClick={(hashtag) => navigate(`/cotations/hashtag/${generateSlug(hashtag)}`)}
              defaultRevaloExpanded
            />
          </div>

          <div className="mb-8">
            <EmbeddedRevenueSimulator act={act} darkMode={darkMode} />
          </div>

          <div className="mb-8">
            <SeoContentBlock act={act} darkMode={darkMode} />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// ThemePage component (previously in AppRouter)
const ThemePage = () => {
  const { theme_slug } = useParams<{ theme_slug: string }>();
  const { state, dispatch } = useAppContext();
  const { acts } = state.data;
  const { cartItems } = state.searchAndFilter;
  const { darkMode } = state.ui;
  const navigate = useNavigate();
  
  const [themeActs, setThemeActs] = useState<any[]>([]);
  const [hasTrackedPageView, setHasTrackedPageView] = useState(false);

  useEffect(() => {
    if (theme_slug) {
      const theme = deslugifyTheme(theme_slug);
      const actsForTheme = acts.filter(act => 
        act.theme && generateSlug(act.theme) === theme_slug
      );
      setThemeActs(actsForTheme);
      
      if (actsForTheme.length > 0) {
        const meta = generateThemePageMeta(theme, actsForTheme);
        updatePageMeta(undefined, meta);

        // Update JSON-LD structured data
        const jsonLdSchema = generateThemeSchema(theme, actsForTheme);
        updateJsonLd(jsonLdSchema, 'theme-page-jsonld');

        // Track page view only once
        if (!hasTrackedPageView) {
          trackPageView(`Thème ${theme}`, `/themes/${theme_slug}`);
          trackThemePageViewed(theme_slug || theme);
          setHasTrackedPageView(true);
        }
      }
    }
  }, [theme_slug, acts, hasTrackedPageView]);

  const theme = theme_slug ? deslugifyTheme(theme_slug) : '';

  return (
    <div className="min-h-screen transition-colors duration-300" style={{
      background: darkMode 
        ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <AppinesBanner className="md:pt-6" />
      
      <div className="pt-20 md:pt-0 px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb items={[]} darkMode={darkMode} />
          
          <div className="text-center mb-8">
            <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {theme}
            </h1>
            <p className={`text-lg ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {themeActs.length} acte{themeActs.length > 1 ? 's' : ''} disponible{themeActs.length > 1 ? 's' : ''}
            </p>
          </div>

          <ResultsGrid
            results={themeActs}
            cartItems={cartItems}
            onToggleCartItem={(act) => dispatch({ type: 'TOGGLE_CART_ITEM', payload: act })}
            darkMode={darkMode}
            loading={false}
            displayCotationType={state.searchAndFilter.displayCotationType}
            onHashtagClick={(hashtag) => navigate(`/cotations/hashtag/${generateSlug(hashtag)}`)}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { darkMode, showMobileFilters } = state.ui;
  const { loading, error } = state.data;
  const { searchQuery, filters, cartItems, currentPage, displayCotationType } = state.searchAndFilter;

  const { handleToggleCartItem } = useCart();
  const { filteredActs, handleSearchChange, handleSearchSubmitted } = useSearch();
  const { availableData, handleFiltersChange, handleVignetteToggle, handleClearAllVignettes } = useFilters();

  const paginatedActs = useMemo(() => {
    const startIndex = (currentPage - 1) * APP_CONFIG.ITEMS_PER_PAGE;
    const endIndex = startIndex + APP_CONFIG.ITEMS_PER_PAGE;
    return filteredActs.slice(startIndex, endIndex);
  }, [filteredActs, currentPage]);

  const totalPages = Math.ceil(filteredActs.length / APP_CONFIG.ITEMS_PER_PAGE);

  const handleHashtagClick = useCallback((hashtag: string) => {
    handleSearchChange(hashtag);
    handleSearchSubmitted(hashtag);
    trackHashtagClicked(hashtag);
  }, [handleSearchChange, handleSearchSubmitted]);

  useEffect(() => {
    updatePageMeta();
    trackPageView('Home Page', '/');
  }, []);

  return (
    <div className="min-h-screen transition-colors duration-300" style={{
      background: darkMode
        ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <AppinesBanner className="md:pt-6" />

      <div className="pt-20 md:pt-0 px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              NGAP Kiné - Simulateur de Cotations
            </h1>
            <p className={`text-lg md:text-xl mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Nomenclature Générale des Actes Professionnels pour Kinésithérapeutes
            </p>
            <p className={`text-base ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Trouvez rapidement les cotations de vos actes de kinésithérapie selon le référentiel NGAP en vigueur
            </p>

            <div className="mt-6">
              <button
                onClick={() => navigate('/simulateur')}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <Calculator className="h-5 w-5 mr-2" />
                Simulateur de Revenus
                <span className="ml-2 text-xs bg-white/20 px-2 py-1 rounded-full">
                  Nouveau
                </span>
              </button>
            </div>
          </div>

          <div className="mb-6">
            <SearchBar
              initialValue={searchQuery}
              onGlobalSearchChange={handleSearchChange}
              onSearchSubmitted={handleSearchSubmitted}
              placeholder={MESSAGES.LABELS.SEARCH_PLACEHOLDER}
              darkMode={darkMode}
            />
          </div>

          <VisualFilterVignettes
            selectedVignettes={filters.selected_vignettes}
            onVignetteToggle={handleVignetteToggle}
            onClearAll={handleClearAllVignettes}
            darkMode={darkMode}
            availableVignettes={availableData.available_vignettes}
          />

          <div className="hidden lg:flex gap-6 mt-8 mb-4">
            <div className="lg:w-80 flex items-center">
              <h2 className={`font-semibold flex items-center text-lg ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <Filter className="h-5 w-5 mr-2" />
                Filtres
              </h2>
            </div>
            <div className="flex-1 flex items-center justify-between">
              <h2 className={`font-semibold flex items-center text-lg ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <LayoutGrid className="h-5 w-5 mr-2" />
                Résultats
                <span className={`ml-2 text-sm font-normal ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  ({filteredActs.length})
                </span>
              </h2>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 mt-4 lg:mt-0">
            <div className="lg:w-80 space-y-6">
              <div className="lg:hidden">
                <button
                  onClick={() => dispatch({ type: 'SET_SHOW_MOBILE_FILTERS', payload: true })}
                  className={`w-full flex items-center justify-center px-4 py-3 rounded-lg border-2 border-dashed transition-colors ${
                    darkMode
                      ? 'border-gray-600 text-gray-300 hover:border-gray-500 hover:text-white'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-800'
                  }`}
                >
                  <Filter className="h-5 w-5 mr-2" />
                  Filtres avancés
                </button>
              </div>

              <div className="hidden lg:block">
                <FilterPanel
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  isVisible={true}
                  darkMode={darkMode}
                  availableData={availableData}
                />
              </div>
            </div>

            <div className="flex-1">
              {error ? (
                <div className={`text-center py-12 rounded-lg ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className={`text-6xl mb-4 ${
                    darkMode ? 'text-red-400' : 'text-red-500'
                  }`}>
                    ⚠️
                  </div>
                  <h3 className={`text-xl font-semibold mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {MESSAGES.ERRORS.CONNECTION}
                  </h3>
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                    {MESSAGES.ERRORS.CONNECTION_DETAIL}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    {MESSAGES.BUTTONS.RETRY}
                  </button>
                </div>
              ) : (
                <>
                  <h2 className={`lg:hidden font-semibold flex items-center text-lg mb-4 ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    <LayoutGrid className="h-5 w-5 mr-2" />
                    Résultats
                    <span className={`ml-2 text-sm font-normal ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      ({filteredActs.length})
                    </span>
                  </h2>
                  <ResultsGrid
                    results={paginatedActs}
                    cartItems={cartItems}
                    onToggleCartItem={handleToggleCartItem}
                    darkMode={darkMode}
                    loading={loading}
                    displayCotationType={displayCotationType}
                    onHashtagClick={handleHashtagClick}
                  />

                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => dispatch({ type: 'SET_CURRENT_PAGE', payload: page })}
                      darkMode={darkMode}
                      totalItems={filteredActs.length}
                      itemsPerPage={APP_CONFIG.ITEMS_PER_PAGE}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-40">
        <button
          onClick={() => dispatch({ type: 'TOGGLE_DARK_MODE' })}
          className={`p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900 ${
            darkMode
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-800 text-white'
          }`}
          title={darkMode ? 'Mode clair' : 'Mode sombre'}
          aria-label={darkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
          aria-pressed={darkMode}
        >
          {darkMode ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
        </button>
      </div>

      <MobileFilterDrawer
        isOpen={showMobileFilters}
        onClose={() => dispatch({ type: 'SET_SHOW_MOBILE_FILTERS', payload: false })}
        darkMode={darkMode}
      >
        <FilterPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          isVisible={true}
          darkMode={darkMode}
          isMobileDrawer={true}
          onClose={() => dispatch({ type: 'SET_SHOW_MOBILE_FILTERS', payload: false })}
          availableData={availableData}
        />
      </MobileFilterDrawer>
    </div>
  );
};

function App() {
  // Get global state and dispatch
  const { state, dispatch } = useAppContext();
  const { darkMode } = state.ui;
  const { acts, loading } = state.data;
  const { filters, cartItems, currentPage, displayCotationType } = state.searchAndFilter;

  const { handleToggleCartItem } = useCart();
  const { filteredActs, handleSearchChange, handleSearchSubmitted } = useSearch();
  const { handleFiltersChange } = useFilters();

  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Gestion des redirections automatiques pour les URLs obsolètes
  useEffect(() => {
    const autoRedirect = detectObsoleteUrlPattern(location.pathname);
    if (autoRedirect && autoRedirect !== location.pathname) {
      logger.info(`Auto-redirecting from ${location.pathname} to ${autoRedirect}`);
      navigate(autoRedirect, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Initialize performance monitoring
  useEffect(() => {
    initPerformanceMonitoring();
    
    // Preload critical images
    preloadImages(CRITICAL_IMAGES).catch(console.warn);
    
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  // Load data from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        const connectionTest = await testSupabaseConnection();

        if (!connectionTest.success) {
          throw new Error(`Connection failed: ${connectionTest.error}`);
        }

        const { data, error: supabaseError } = await supabase
          .from('ngap_cotations')
          .select('*')
          .order('lettre_cle', { ascending: true })
          .order('cotation_valeur', { ascending: true });

        if (supabaseError) {
          throw supabaseError;
        }

        if (data) {
          dispatch({ type: 'SET_ACTS', payload: data });

          const pathologyHashtagsSet = new Set<string>();
          
          data.forEach(act => {
            if (act.pathologies && act.pathologies.trim()) {
              // Split pathologies by comma and process each one
              const pathologies = act.pathologies.split(',');
              pathologies.forEach(pathology => {
                const trimmedPathology = pathology.trim();
                if (trimmedPathology) {
                  pathologyHashtagsSet.add(trimmedPathology);
                }
              });
            }
          });
          
          const sortedPathologyHashtags = Array.from(pathologyHashtagsSet).sort();
          dispatch({ type: 'SET_PATHOLOGY_HASHTAGS', payload: sortedPathologyHashtags });
          
          success(
            MESSAGES.SUCCESS.DATA_LOADED,
            `${data.length} ${MESSAGES.SUCCESS.DATA_LOADED_DETAIL}`
          );
          
          // Track successful data load
          trackEvent('data_load', 'success', 'ngap_acts', data.length);
          
          // Track pathology hashtags count
          trackEvent('data_load', 'pathology_hashtags', 'count', sortedPathologyHashtags.length);
        }
      } catch (err) {
        logger.error('Error loading data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        showError(
          MESSAGES.ERRORS.LOADING,
          `${MESSAGES.ERRORS.LOADING_DETAIL} ${errorMessage.includes('Connection failed') ? 'Vérifiez votre configuration Supabase.' : ''}`
        );
        
        // Track error
        trackEvent('data_load', 'error', errorMessage);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination logic
  const paginatedActs = useMemo(() => {
    const startIndex = (currentPage - 1) * APP_CONFIG.ITEMS_PER_PAGE;
    const endIndex = startIndex + APP_CONFIG.ITEMS_PER_PAGE;
    return filteredActs.slice(startIndex, endIndex);
  }, [filteredActs, currentPage]);

  const totalPages = Math.ceil(filteredActs.length / APP_CONFIG.ITEMS_PER_PAGE);

  // Hashtag click handler
  const handleHashtagClick = useCallback((hashtag: string) => {
    handleSearchChange(hashtag);
    handleSearchSubmitted(hashtag);
    
    // Track hashtag click
    trackHashtagClicked(hashtag);
  }, [handleSearchChange, handleSearchSubmitted]);

  // Cookie consent handlers
  const handleCookieAccept = useCallback(() => {
    initializeGoogleAnalytics();
    window.setTimeout(() => initializeGoogleAds(), 500);
    trackEvent('consent', 'cookie_consent', 'accepted');
  }, []);

  const handleCookieDecline = useCallback(() => {
    disableGoogleAnalytics();
    trackEvent('consent', 'cookie_consent', 'declined');
  }, []);


  return (
    <ErrorBoundary>
      <div className={darkMode ? 'dark' : ''}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          
          {/* Route pour les lettres clés avec préfixe actes (ex: /cotations/actes/apm) */}
          <Route path="/cotations/actes/:letter_key" element={
            <CotationPage
              acts={acts}
              loading={loading}
              filters={filters}
              handleFiltersChange={handleFiltersChange}
              darkMode={darkMode}
              filteredActs={filteredActs}
              paginatedActs={paginatedActs}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={(page) => dispatch({ type: 'SET_CURRENT_PAGE', payload: page })}
              itemsPerPage={APP_CONFIG.ITEMS_PER_PAGE}
              cartItems={cartItems}
              onToggleCartItem={handleToggleCartItem}
              displayCotationType={displayCotationType}
              onHashtagClick={handleHashtagClick}
            />
          } />

          {/* Route pour les cotations spécifiques avec valeurs (ex: /cotations/amk-7-5) */}
          <Route path="/cotations/:cotation_slug_value" element={
            <CotationValuePage
              acts={acts}
              cartItems={cartItems}
              onToggleCartItem={handleToggleCartItem}
              darkMode={darkMode}
              displayCotationType={displayCotationType}
              onHashtagClick={handleHashtagClick}
              loading={loading}
            />
          } />
          
          <Route path="/cotations/:letter_key/:act_slug" element={<ActPage />} />
          
          <Route path="/cotations/hashtag/:hashtag_slug" element={
            <HashtagPage
              darkMode={darkMode}
              cartItems={cartItems}
              onToggleCartItem={handleToggleCartItem}
              displayCotationType={displayCotationType}
              onHashtagClick={handleHashtagClick}
            />
          } />
          
          <Route path="/themes/:theme_slug" element={<ThemePage />} />

          <Route path="/simulateur" element={<SimulatorPage />} />

          {/* Routes pour les revalorisations */}
          <Route path="/revalorisations/:year" element={<RevalorisationsPage darkMode={darkMode} />} />

          {/* Route 404 - doit être la dernière */}
          <Route path="*" element={<NotFoundPage darkMode={darkMode} />} />
        </Routes>

        {/* Cookie Consent Banner - Global for all pages */}
        <CookieConsentBanner
          onAccept={handleCookieAccept}
          onDecline={handleCookieDecline}
        />

        {/* Sticky Simulator CTA - Only on homepage */}
        <Routes>
          <Route path="/" element={<StickySimulatorCTA darkMode={darkMode} />} />
        </Routes>
        
        {/* Toast Container */}
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {state.toast.toasts.map(toast => (
            <Toast
              key={toast.id}
              toast={toast}
              onClose={(id) => dispatch({ type: 'REMOVE_TOAST', payload: id })}
              darkMode={darkMode}
            />
          ))}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;